import { Ref, ref, inject, computed, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { ValidationOptions } from "../types/InputValidation.type";
import { SharedHTMLInputElement, SharedHTMLSelectElement, SharedHTMLTextAreaElement } from "../types/SharedInput.type";
import { useFileValidation } from "./use-file-validation";

export default function useValidationText(
	elementRef: Ref<SharedHTMLTextAreaElement | SharedHTMLSelectElement | SharedHTMLInputElement | null>,
	displayName: string,
	options?: ValidationOptions,
	externalErrorMessage?: Ref<string | undefined>,
	requiredErrorMessage?: string
) {
	const { t } = useI18n();

	const { isFileSizeError, isFileTypeError, getFormattedAcceptString } = useFileValidation(
		elementRef as Ref<HTMLInputElement>,
		options
	);

	const numberOfSubmits = inject("numberOfSubmits", ref(0));
	const formValidationResetControl = inject("formValidationResetControl", ref(0));
	const hasFormWrapper = inject("hasFormWrapper", false);
	const updateFormValidity: () => boolean | void = inject("updateFormValidity", () => {});

	const requiredError = ref(false);
	const patternError = ref(false);
	const typeError = ref(false);
	const fileTypeError = ref(false);
	const fileSizeError = ref(false);
	const rangeOverflowError = ref(false);
	const rangeUnderflowError = ref(false);
	const tooLongError = ref(false);
	const tooShortError = ref(false);
	const badInputError = ref(false);

	const isLiveValidation = ref(false);
	const isValidationVisible = computed(() => {
		const hasExternalErrorMessage = !!externalErrorMessage?.value;
		const hasInternalErrorMessage = !!errorMessage.value;
		const wasSubmitted = numberOfSubmits.value > 0;
		const internalValidationActive = isLiveValidation.value || wasSubmitted;

		return hasExternalErrorMessage || (hasInternalErrorMessage && internalValidationActive);
	});

	// Helfer für initialLiveValidation, damit bei Validation Reset (formValidationResetControl) die Validierung nicht kurz "aufblitzt"
	let initialLiveValidationTimeout = 0;

	const updateValidateStates = () => {
		if (!elementRef.value) return;

		// patternMissmatch hat höhere Priorität wie typeMismatch (z.B. wichtig bei E-Mail-Pattern)
		const hasPattern = elementRef.value?.getAttribute("pattern");

		requiredError.value = elementRef.value.validity.valueMissing;
		rangeOverflowError.value = elementRef.value.validity.rangeOverflow;
		rangeUnderflowError.value = elementRef.value.validity.rangeUnderflow;
		tooLongError.value = elementRef.value.validity.tooLong;
		tooShortError.value = elementRef.value.validity.tooShort;
		badInputError.value = elementRef.value.validity.badInput;
		patternError.value = elementRef.value.validity.patternMismatch;
		typeError.value = hasPattern ? false : elementRef.value.validity.typeMismatch;
		fileTypeError.value = isFileTypeError();
		fileSizeError.value = isFileSizeError();
	};

	// eslint-disable-next-line complexity
	const errorMessage = computed((): string | undefined => {
		if (externalErrorMessage?.value) return externalErrorMessage?.value;
		if (requiredError.value) return requiredErrorMessage || t("shared.validation.requiredError", [displayName]);
		if (patternError.value) return options?.patternErrorMessage || t("shared.validation.invalidError", [displayName]);
		if (typeError.value) return t("shared.validation.invalidError", [displayName]);
		if (fileTypeError.value) return t("shared.validation.fileTypeError", [getFormattedAcceptString()]);
		if (fileSizeError.value) return t("shared.validation.fileSizeError", [options?.maxFileSizeMb]);
		if (rangeOverflowError.value)
			return elementRef.value?.type === "date"
				? t("shared.validation.rangeOverflowDateError", [displayName])
				: t("shared.validation.rangeOverflowError", [displayName]);
		if (rangeUnderflowError.value)
			return elementRef.value?.type === "date"
				? t("shared.validation.rangeUnderflowDateError", [displayName])
				: t("shared.validation.rangeUnderflowError", [displayName]);
		if (tooLongError.value) return t("shared.validation.tooLongError", [displayName]);
		if (tooShortError.value) return t("shared.validation.tooShortError", [displayName]);
		if (badInputError.value) return t("shared.validation.invalidError", [displayName]);

		return undefined;
	});

	// Update errorMessage on the input
	watch(
		() => errorMessage.value,
		() => {
			if (!elementRef.value) return;
			elementRef.value.errorMessage = errorMessage.value;
			const newValidity = errorMessage.value || "";
			elementRef.value.setCustomValidity(newValidity);
		}
	);

	watch(
		() => numberOfSubmits.value,
		(isSubmitTriggered) => {
			if (isSubmitTriggered) updateValidateStates();
		}
	);

	watch(
		() => formValidationResetControl.value,
		() => {
			window.clearTimeout(initialLiveValidationTimeout);
			initialLiveValidationTimeout = 0;
			isLiveValidation.value = false;
		}
	);

	watch(
		() => isValidationVisible.value,
		() => {
			nextTick(updateFormValidity); // next tick to before update the computed error message
		}
	);

	const handleInput = () => nextTick(updateValidateStates);

	const handleChange = (startLiveValidation?: boolean) => {
		updateValidateStates();
		if ((startLiveValidation || !hasFormWrapper) && errorMessage.value) {
			doLiveValidation();
		}
	};

	const handleBlur = handleChange;

	const doLiveValidation = () => {
		// delay live validation to prevent flickering on login layer/landing pages (switching between login and register)
		if (!initialLiveValidationTimeout && !isLiveValidation.value) {
			initialLiveValidationTimeout = window.setTimeout(() => {
				isLiveValidation.value = true;
			}, 180);
		}
	};

	return {
		errorMessage,
		handleChange,
		handleBlur,
		handleInput,
		isValidationVisible,
	};
}
